import { type Task, task, Unit } from 'true-myth';

export interface Reporter {
  count?: (name: string, amount: number) => Task<unknown, string>;
  gauges?: (name: string, amount: number) => Task<unknown, string>;
  measurementWithGauges?: (name: string, amount: number) => Task<unknown, string>;
  error?: (args: { error: Error }) => Task<unknown, string>;
  /** Force any buffered metrics out now — for page teardown, where a throttled writer's trailing flush never fires. */
  flush?: () => Task<unknown, string>;
}

export function defineReporter(reporter: Reporter) {
  return reporter;
}

export interface Count {
  name: string;
  amount?: number;
}

export interface Measure {
  name: string;
  amount: number;
}

export class Monitor {
  constructor(protected readonly reporters: Array<Reporter> = []) {}

  count(...counts: Array<Count>) {
    return task.all(
      counts.flatMap(({ name, amount = 1 }) =>
        this.reporters.map((reporter) => (reporter.count?.(name, amount) ?? task.resolve()).map(() => Unit)),
      ),
    );
  }

  gauges(...measures: Array<Measure>) {
    return task.all(
      measures.flatMap(({ name, amount }) =>
        this.reporters.map((reporter) => (reporter.gauges?.(name, amount) ?? task.resolve()).map(() => Unit)),
      ),
    );
  }

  measurementWithGauges(...measures: Array<Measure>) {
    return task.all(
      measures.flatMap(({ name, amount }) =>
        this.reporters.map((reporter) =>
          (reporter.measurementWithGauges?.(name, amount) ?? task.resolve()).map(() => Unit),
        ),
      ),
    );
  }

  error(arg: { error: Error } & Record<string, unknown>) {
    return task.all(this.reporters.map((reporter) => (reporter.error?.(arg) ?? task.resolve()).map(() => Unit)));
  }

  flush() {
    return task.all(this.reporters.map((reporter) => (reporter.flush?.() ?? task.resolve()).map(() => Unit)));
  }
}
